import { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart3, Megaphone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if consent was already given
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const getSessionId = () => {
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  };

  const saveConsent = async (consentData) => {
    try {
      await axios.post(`${API}/cookie-consent`, {
        session_id: getSessionId(),
        ...consentData,
      });
    } catch (error) {
      console.error('Failed to save cookie consent:', error);
    }
  };

  const handleAcceptAll = async () => {
    const consent = { necessary: true, analytics: true, marketing: true };
    setPreferences(consent);
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    await saveConsent(consent);
    setIsVisible(false);
  };

  const handleRejectAll = async () => {
    const consent = { necessary: true, analytics: false, marketing: false };
    setPreferences(consent);
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    await saveConsent(consent);
    setIsVisible(false);
  };

  const handleSavePreferences = async () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    await saveConsent(preferences);
    setIsVisible(false);
    setShowPreferences(false);
  };

  if (!isVisible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 cookie-banner"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden">
          {!showPreferences ? (
            /* Main Banner */
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-indigo-50">
                  <Cookie className="h-6 w-6 text-indigo-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-950 mb-2">
                    We value your privacy
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                    By clicking "Accept All", you consent to our use of cookies. 
                    Read our{' '}
                    <a href="/legal/cookies" className="text-indigo-600 hover:underline">
                      Cookie Policy
                    </a>{' '}
                    for more information.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      data-testid="cookie-accept-all"
                      onClick={handleAcceptAll}
                      className="px-6 py-2.5 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-button transition-all"
                    >
                      Accept All
                    </button>
                    <button
                      data-testid="cookie-reject-all"
                      onClick={handleRejectAll}
                      className="px-6 py-2.5 text-sm font-medium rounded-full text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all"
                    >
                      Reject All
                    </button>
                    <button
                      data-testid="cookie-customize"
                      onClick={() => setShowPreferences(true)}
                      className="px-6 py-2.5 text-sm font-medium rounded-full text-slate-600 border border-slate-200 hover:bg-slate-50 transition-all"
                    >
                      Customize
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Preferences Panel */
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-950">
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-4 rounded-2xl bg-slate-50">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-white">
                      <Shield className="h-5 w-5 text-slate-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-950">Necessary</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Essential for the website to function properly. Cannot be disabled.
                      </p>
                    </div>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-slate-200 text-xs font-medium text-slate-600">
                    Always On
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 rounded-2xl border border-slate-100">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-slate-50">
                      <BarChart3 className="h-5 w-5 text-slate-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-950">Analytics</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Help us understand how visitors interact with our website.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                    data-testid="cookie-analytics-toggle"
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      preferences.analytics ? 'bg-indigo-600' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full transition-transform ${
                        preferences.analytics ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-4 rounded-2xl border border-slate-100">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-slate-50">
                      <Megaphone className="h-5 w-5 text-slate-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-950">Marketing</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Used to deliver personalized advertisements.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                    data-testid="cookie-marketing-toggle"
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      preferences.marketing ? 'bg-indigo-600' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full transition-transform ${
                        preferences.marketing ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="cookie-save-preferences"
                  onClick={handleSavePreferences}
                  className="flex-1 px-6 py-3 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-button transition-all"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
